import { Modal, Button } from 'antd';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import ReactCopyToClipboard from 'react-copy-to-clipboard';
import toast from 'react-hot-toast';

import { LocalImage } from '..';
import styles from './index.module.scss';
import { postQueryPictureStyleDetail } from '../../../api/material';
import { useImageIcon } from '../../../assets/ImageCreation';
import { copiedIcon, copyIcon, inpaintIcon, recreateIcon } from '../../../assets/ImageLibrary';
import { getCfgScaleLevel } from '../../../constants/image';
import { useImageModels } from '../../../hooks';
import { useStyle } from '../../../hooks/useStyle';
import { useAppDispatch, useAppSelector } from '../../../state';
import { queryStyleDetail } from '../../../state/image';
import { track } from '../../../track';
import { INpcAvatar } from '../../../types';
import { OutlineBtn, PrimaryBtn } from '../../Components/PrimaryBtn';

interface DetailModalProps {
  avatarData: INpcAvatar;
  visible: boolean;
  onClose: () => void;
  disableRecreate?: boolean;
  onInpaint?: () => void;
  onCreateNpc?: () => void;
  onRecreate?: () => void;
  source: string;
  materialId?: number;
}

function ModalInfo({ avatarData }: { avatarData: INpcAvatar }) {
  const hasModelInfo = !!avatarData.web_preview_extra?.img_model_id;
  const models = useImageModels();
  const model = models.models.find(
    (model) => model.id === avatarData.web_preview_extra?.img_model_id
  );
  const hasModel = !!model;
  const { t } = useTranslation();
  if (!hasModelInfo || !hasModel) {
    return null;
  }
  return (
    <div className={styles.item}>
      <div className={styles.title}>{t('model')}</div>
      <div className={styles.content}>{model.name}</div>
    </div>
  );
}

function CFGInfo({ avatarData }: { avatarData: INpcAvatar }) {
  const hasCFGInfo = avatarData.web_preview_extra?.cfg_scale !== undefined;
  const models = useImageModels();
  const model = models.models.find(
    (model) => model.id === avatarData.web_preview_extra?.img_model_id
  );
  const hasModel = !!model;
  const { t } = useTranslation();

  if (!hasCFGInfo || !hasModel) {
    return null;
  }
  return (
    <div className={styles.item}>
      <div className={styles.title}>{t('cfg_scale')}</div>
      <div className={styles.content}>
        {t(`cfg_scale_${getCfgScaleLevel(model.type, avatarData.web_preview_extra?.cfg_scale!)}`)}
      </div>
    </div>
  );
}

function StyleReferenceInfo({ avatarData }: { avatarData: INpcAvatar }) {
  const hasStyleReference =
    !!avatarData.avatar_style_id || !!avatarData.web_preview_extra?.style_img_url;
  const { styleName, picUrl } = useStyle(avatarData);
  const { t } = useTranslation();
  if (!hasStyleReference) {
    return null;
  }
  return (
    <div className={styles.item}>
      <div className={styles.title}>{t('style_reference')}</div>
      <div className={styles.content}>
        {!!avatarData.web_preview_extra?.style_img_url && (
          <img src={avatarData.web_preview_extra?.style_img_url} alt="Style Reference" />
        )}
        {!avatarData.web_preview_extra?.style_img_url && !!picUrl && (
          <img src={picUrl} alt="Style Reference" />
        )}
        <p>{styleName}</p>
        <p>{avatarData.web_preview_extra?.style_weight || 0}</p>
      </div>
    </div>
  );
}

function FaceReferenceInfo({ avatarData }: { avatarData: INpcAvatar }) {
  const hasFaceReference = !!avatarData.web_preview_extra?.face_img_url;
  const { t } = useTranslation();
  if (!hasFaceReference) {
    return null;
  }
  return (
    <div className={styles.item}>
      <div className={styles.title}>{t('face_reference')}</div>
      <div className={styles.content}>
        <img src={avatarData.web_preview_extra?.face_img_url} alt="Face Reference" />
        {avatarData.web_preview_extra?.face_weight || 0}
      </div>
    </div>
  );
}

function CopyPrompt({
  avatarData,
  source,
  materialId,
}: {
  avatarData: INpcAvatar;
  source: string;
  materialId: number | undefined;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <ReactCopyToClipboard
      text={avatarData.img_desc!}
      onCopy={(_, result) => {
        if (result) {
          setCopied(true);
          track.send('copy_prompt_click', source, {
            material_id: materialId,
            view: 'detail_image_popup_wnd',
          });
        }
      }}
    >
      <LocalImage className={styles.copyIcon} src={copied ? copiedIcon : copyIcon} alt="Copy" />
    </ReactCopyToClipboard>
  );
}

const DetailModal: React.FC<DetailModalProps> = ({
  visible,
  onClose,
  avatarData,
  disableRecreate,
  onInpaint,
  onCreateNpc,
  onRecreate,
  source,
  materialId,
}) => {
  const hasPrompt = avatarData.img_desc && avatarData.img_desc.trim() !== '';
  const { t } = useTranslation();
  const webStyleMap = useAppSelector((state) => state.imageLibrary.webStyleMap);
  const isWebStyle = !!webStyleMap[avatarData.avatar_style_id!];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      classNames={{
        content: styles.detailModal,
      }}
      closeIcon={null}
      destroyOnClose
      centered
    >
      <div className={styles.modalContent}>
        <div className={styles.imageSection}>
          <img src={avatarData.avatar_img_url} alt="Generated Image" />
        </div>
        <div className={styles.infoSection}>
          {hasPrompt && (
            <>
              <div className={styles.promptInfo}>
                <div className={styles.title}>
                  <h3>{t('prompt')}</h3>
                  <CopyPrompt avatarData={avatarData} source={source} materialId={materialId} />
                </div>
                <p>{avatarData.img_desc}</p>
              </div>
              <div className={styles.divider} />
            </>
          )}
          <div className={styles.basicSettings}>
            <ModalInfo avatarData={avatarData} />
            <CFGInfo avatarData={avatarData} />
            <StyleReferenceInfo avatarData={avatarData} />
            <FaceReferenceInfo avatarData={avatarData} />
          </div>
          {disableRecreate ? (
            <div className={styles.actionButtons}>
              <div className="flex gap-[10px] mb-[10px]">
                <OutlineBtn
                  icon={inpaintIcon}
                  text={t('inpaint')}
                  onClick={() => {
                    onInpaint?.();
                    onClose();
                  }}
                  width={'50%'}
                />
                <PrimaryBtn
                  icon={useImageIcon}
                  text={t('use_image')}
                  onClick={() => {
                    onCreateNpc?.();
                    onClose();
                  }}
                  width={'50%'}
                />
              </div>
            </div>
          ) : (
            <div className={styles.actionButtons}>
              {isWebStyle && (
                <div className="flex gap-[10px] mb-[10px]">
                  <OutlineBtn
                    icon={inpaintIcon}
                    text={t('inpaint')}
                    onClick={() => {
                      onInpaint?.();
                      onClose();
                    }}
                    width={'50%'}
                  />
                  <OutlineBtn
                    icon={recreateIcon}
                    text={t('recreate')}
                    width={'50%'}
                    onClick={() => {
                      onRecreate?.();
                      onClose();
                    }}
                  />
                </div>
              )}
              <PrimaryBtn
                icon={useImageIcon}
                text={t('create_a_npc')}
                onClick={() => {
                  onCreateNpc?.();
                  onClose();
                }}
                width={'100%'}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DetailModal;
